// Native Intl-based date utilities. Zero dependencies. Replaces moment.js + luxon.
// All formatting renders in the runtime's local timezone (matching moment's default).

const MS_PER_SECOND = 1000;
const MS_PER_HOUR = 3600000;
const INVALID_DATE = 'Invalid date';

// Normalise Date | string | number -> Date. Numbers are milliseconds (like moment(n)).
function toDate(value) {
    return value instanceof Date ? value : new Date(value);
}

// --- Unix / conversion ------------------------------------------------------

export function nowUnix() {
    return Math.floor(Date.now() / MS_PER_SECOND);
}

export function unixToDate(seconds) {
    return new Date(Number(seconds) * MS_PER_SECOND);
}

export function dateToUnix(value) {
    return Math.floor(toDate(value).getTime() / MS_PER_SECOND);
}

// --- Math / comparison ------------------------------------------------------

export function addMonths(value, months) {
    const d = toDate(value);
    const day = d.getDate();
    const result = new Date(d); // clone: preserves time-of-day
    result.setDate(1); // avoid day-overflow while shifting the month
    result.setMonth(result.getMonth() + months);
    const daysInTargetMonth = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
    result.setDate(Math.min(day, daysInTargetMonth)); // clamp to end-of-month, like moment
    return result;
}

export function diffInHours(a, b = Date.now()) {
    return Math.trunc((toDate(a).getTime() - toDate(b).getTime()) / MS_PER_HOUR);
}

export function isValidDate(value) {
    return !Number.isNaN(toDate(value).getTime());
}

export function isSameCalendarDay(a, b) {
    const da = toDate(a);
    const db = toDate(b);
    return da.getFullYear() === db.getFullYear() && da.getMonth() === db.getMonth() && da.getDate() === db.getDate();
}

export function isSameYear(a, b) {
    return toDate(a).getFullYear() === toDate(b).getFullYear();
}

// --- Formatting -------------------------------------------------------------

const ORDINAL_RULES = new Intl.PluralRules('en', { type: 'ordinal' });
const ORDINAL_SUFFIX = { one: 'st', two: 'nd', few: 'rd', other: 'th', zero: 'th', many: 'th' };
const MONTH_LONG = new Intl.DateTimeFormat('en', { month: 'long' });
const MONTH_SHORT = new Intl.DateTimeFormat('en', { month: 'short' });
const WEEKDAY_LONG = new Intl.DateTimeFormat('en', { weekday: 'long' });

function ordinal(n) {
    return `${n}${ORDINAL_SUFFIX[ORDINAL_RULES.select(n)]}`;
}

function dayPart(d, style) {
    const n = d.getDate();
    if (style === 'ordinal') {
        return ordinal(n);
    }
    if (style === '2-digit') {
        return String(n).padStart(2, '0');
    }
    return String(n);
}

function yearPart(d, style) {
    if (style === '2-digit') {
        return String(d.getFullYear()).slice(-2);
    }
    return String(d.getFullYear());
}

// Assembles "<day> <month> [year]" in British day-first order.
function core(value, { day, month, year }) {
    const d = toDate(value);
    if (Number.isNaN(d.getTime())) {
        return INVALID_DATE;
    }
    const monthName = (month === 'short' ? MONTH_SHORT : MONTH_LONG).format(d);
    const parts = [dayPart(d, day), monthName];
    if (year) {
        parts.push(yearPart(d, year));
    }
    return parts.join(' ');
}

export function formatOrdinalDate(value) {
    return core(value, { day: 'ordinal', month: 'long', year: 'numeric' });
}

export function formatOrdinalDateShortYear(value) {
    return core(value, { day: 'ordinal', month: 'long', year: '2-digit' });
}

export function formatOrdinalShortMonthYear(value) {
    return core(value, { day: 'ordinal', month: 'short', year: 'numeric' });
}

export function formatOrdinalShortMonthShortYear(value) {
    return core(value, { day: 'ordinal', month: 'short', year: '2-digit' });
}

export function formatDayMonth(value) {
    return core(value, { day: '2-digit', month: 'long' });
}

export function formatDayMonthYear(value) {
    return core(value, { day: '2-digit', month: 'long', year: 'numeric' });
}

export function formatDayShortMonthYear(value) {
    return core(value, { day: '2-digit', month: 'short', year: 'numeric' });
}

export function formatShortMonth(value) {
    const d = toDate(value);
    if (Number.isNaN(d.getTime())) {
        return INVALID_DATE;
    }
    return MONTH_SHORT.format(d);
}

// US month-first order, matching moment's `ll` (e.g. "Jul 3, 2026").
export function formatMediumDate(value) {
    const d = toDate(value);
    if (Number.isNaN(d.getTime())) {
        return INVALID_DATE;
    }
    return `${MONTH_SHORT.format(d)} ${d.getDate()}, ${d.getFullYear()}`;
}

// "Friday, 3rd July 2026, 2:30:45 pm" (moment `dddd, Do MMMM YYYY, h:mm:ss a`).
export function formatFullDateTime(value) {
    const d = toDate(value);
    if (Number.isNaN(d.getTime())) {
        return INVALID_DATE;
    }
    const hours24 = d.getHours();
    const hours12 = ((hours24 + 11) % 12) + 1;
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');
    const meridiem = hours24 < 12 ? 'am' : 'pm';
    return `${WEEKDAY_LONG.format(d)}, ${formatOrdinalDate(d)}, ${hours12}:${minutes}:${seconds} ${meridiem}`;
}

// --- Relative time ----------------------------------------------------------

const RELATIVE = new Intl.RelativeTimeFormat('en', { numeric: 'always' });

// Returns [roundedValue, unit] (value signed: negative = past), or null when sub-minute.
// Rounds within each bucket and re-tests the ceiling against the ROUNDED value, so rounding
// can't overflow a bucket (e.g. ~59.7 min becomes "1 hour", never "60 minutes").
function relativeParts(value, now) {
    const seconds = (toDate(value).getTime() - now) / MS_PER_SECOND;
    if (Math.abs(seconds) < 60) {
        return null;
    }
    const buckets = [
        [seconds / 60, 60, 'minute'],
        [seconds / 3600, 24, 'hour'],
        [seconds / 86400, 30, 'day'],
        [seconds / (86400 * 30.4368), 12, 'month'],
    ];
    for (const [raw, ceiling, unit] of buckets) {
        const rounded = Math.round(raw);
        if (Math.abs(rounded) < ceiling) {
            return [rounded, unit];
        }
    }
    return [Math.round(seconds / (86400 * 365.25)), 'year'];
}

export function fromNow(value, now = Date.now()) {
    if (Number.isNaN(toDate(value).getTime())) {
        return INVALID_DATE;
    }
    const parts = relativeParts(value, now);
    if (!parts) {
        return 'just now';
    }
    return RELATIVE.format(parts[0], parts[1]);
}

export function timeUntil(value, now = Date.now()) {
    if (Number.isNaN(toDate(value).getTime())) {
        return INVALID_DATE;
    }
    const parts = relativeParts(value, now);
    if (!parts) {
        return 'less than a minute';
    }
    const magnitude = Math.abs(parts[0]);
    return `${magnitude} ${parts[1]}${magnitude === 1 ? '' : 's'}`;
}
